import { BaseAction } from "../../../core/actions/BaseAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { calculatePrice } from "../../../Lib/Utils/validationUtil";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { clearBookingFlowAtoms } from "../../../Orchestrator/Recoils/clearBookingFlowAtoms";
import { TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBookedOffer, IBookingTypeTrip, PassengerPriceDetails } from "../../../Orchestrator/Types/Index";
import { IPaymentItemDetails } from "../../../Orchestrator/Types/Interface/IPayment";
import { IBookingResponse } from "../../../Orchestrator/Types/Types/BookingTypes";
import { calculateAlreadyPaidAddonsAction } from "../../LogicActions/Addons/CalculateAlreadyPaidAddons";
import { cleanupOrphanAddonsAction } from "../../LogicActions/Addons/CleanupOrphanAddonsAction";
import { getBookingAction } from "../../LogicActions/Booking/GetBookingAction";

interface IChangeDateRedirectClickActionResult {
    allFulFillOutBoundPassIds: string[];
    outBoundPassengerPriceList: PassengerPriceDetails[];
    bookingResponse: IBookingResponse;
}
export class ChangeDateRedirectClickAction extends BaseAction<IChangeDateRedirectClickActionResult, {
    bookingId: string,
    route: string,
    tripData: any,
    isShuttle: boolean
}> {
    override async run(): Promise<IChangeDateRedirectClickActionResult | null> {
        let result: IChangeDateRedirectClickActionResult | null = null;
        try {
            const { bookingId, route, tripData, isShuttle } = this.params!;
            // Wipe stale flow state from any prior aborted checkout before
            // seeding this flow's writes (see clearBookingFlowAtoms doc).
            clearBookingFlowAtoms();
            updateRecoilState("selectedPassengerForCancelAtom", []);
            updateRecoilState("selectedPassengerForCancelInBoundAtom", []);
            updateRecoilState("selectedBookingIdForDateChangeAtom", bookingId);
            updateRecoilState("displayPassengerDataAtom", tripData.passengers);
            updateRecoilState("selectedTripsDataAtom", tripData);
            updateRecoilState("selectedRangeStateAtom", [null, null]);
            updateRecoilState("isShuttleChangeDateAtom", isShuttle);

            // Pass filtering parameters to GetBookingAction
            const getBookingParams = {
                bookingId,
                direction: tripData.direction,
                displayPassengerData: tripData.passengers,
                selectedTripsData: tripData
            };
            getBookingAction.setParams(getBookingParams);

            await getBookingAction.execute();
            // Strip orphan PREBOOKED ancillaries from the booking before
            // entering this flow. These come from a prior abandoned manage-
            // seating / addons session — unpaid reservations that would
            // otherwise leak into the change-date pricing math and the
            // trip-summary card. cleanupOrphanAddonsAction is a no-op when
            // there are none, and refreshes bookingResponseAtom on success.
            await cleanupOrphanAddonsAction.execute();
            // If cleanup deleted anything, re-fetch so bookingActionResult
            // reflects the post-cleanup booking. Otherwise the downstream
            // calculateAlreadyPaidAddons math still sees the orphans.
            if (cleanupOrphanAddonsAction.result?.cleaned) {
                getBookingAction.setParams(getBookingParams);
                await getBookingAction.execute();
            }
            if (getBookingAction.status.executionStatus === ExecutionStatus.SUCCESS) {
                const bookingActionResult = getBookingAction.result;
                if (bookingActionResult && bookingActionResult.data) {
                    const bookingResponse = bookingActionResult.data;
                    result = {
                        allFulFillOutBoundPassIds: bookingActionResult.allFulFillOutBoundPassIds,
                        outBoundPassengerPriceList: bookingActionResult.outBoundPassengerPriceList,
                        bookingResponse
                    };
                }

                /**
                 * Manage Addons into booking for booking change trip date.
                 * Checking that existing booking have addons or not.
                 * If have addons then we need to prepare payment summary for already paid addons amount.
                 * That already paid addons amount will be show into payment summary section.
                */
                calculateAlreadyPaidAddonsAction.setParams({
                    allBookedOfferWithoutExchange: bookingActionResult?.allBookedOfferWithoutExchange || [],
                    allBookedTripWithoutExchange: bookingActionResult?.allBookedTripsWithoutExchange || []
                })
                await calculateAlreadyPaidAddonsAction.execute();
            }
            return result;
        } catch (error) {
            console.error("Error during change date redirect click action:", error);
            return result;
        }
    }
}

export const changeDateRedirectClickAction = new ChangeDateRedirectClickAction();
